import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { GiftCard } from '../../../../types/gift-card';

const Container = styled.div`
    margin: 1rem 0 3rem;

    & .carousel-wrapper {
        position: relative;
        overflow: hidden;
        width: 100%;
    }
    
    & .carousel-container {
        display: flex;
        width: max-content;
    }
`;

const Arrow = styled.button<{ left?: boolean }>`
    background: white;
    border: none;
    color: var(--primary);
    cursor: pointer;
    display: flex;
    align-items: center;
    font-size: 1.65rem;
    outline: none;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    z-index: 1;
    width: 1.5rem;
    height: 3rem;
    box-shadow: 0 2px 8px rgba(0,0,0,0.12);
    padding: 0;

    &:focus { outline: none; }

    ${props => props.left ? `
        border-top-right-radius: 2rem;
        border-bottom-right-radius: 2rem;
        border-top-left-radius: 0;
        border-bottom-left-radius: 0;
        justify-content: flex-start;
        left: 0;
    ` : `
        border-top-left-radius: 2rem;
        border-bottom-left-radius: 2rem;
        border-top-right-radius: 0;
        border-bottom-right-radius: 0;
        justify-content: flex-end;
        right: 0;
    `}
`;

const Dots = styled.div`
    position: absolute;
    bottom: 1.5rem;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    gap: 5px;
    z-index: 1;

    em {
        display: inline-block;
        color: white;
        cursor: pointer;
        font-size: 0.55rem;
        margin: 0 4px;
    }
`;

const Card = styled.div<{ selected: boolean }>`
    cursor: pointer;
    margin-left: -15rem;
    transition: margin 0.3s ease;

    &:first-child {
        margin-left: 0;
    }

    ${({ selected }) => selected && `
        margin-right: 13rem;
        
        &:last-child {
            margin-right: 0;
        }
    `}
`;

const CardImage = styled.img`
    width: 21rem;
`;

interface Props {
    items: GiftCard[];
    onClick: (giftCard: GiftCard) => void;
}

function UserWalletListCarousel({ items, onClick }: Props) {
    const containerRef = useRef<HTMLDivElement>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const [currentOffset, setCurrentOffset] = useState(0);
    const [maxOffset, setMaxOffset] = useState(0);
    const [selectedItemIndex, setSelectedItemIndex] = useState<number | null>(null);

    // Calcular dimensiones del contenedor y límites
    const calculateDimensions = useCallback(() => {
        if (!containerRef.current || !wrapperRef.current) return;

        const container = containerRef.current;
        const wrapper = wrapperRef.current;
        
        // Usar el ancho del wrapper en lugar del container
        const containerWidthPx = wrapper.getBoundingClientRect().width;
        const scrollWidthPx = container.scrollWidth;
        
        // Calcular el máximo desplazamiento necesario en píxeles
        const maxOffsetPx = Math.max(0, scrollWidthPx - containerWidthPx);
        
        // Convertir a rem
        const rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize);
        const maxOffsetRem = maxOffsetPx / rootFontSize;
        
        // Redondear hacia arriba al múltiplo de 6rem más cercano
        const maxOffsetIn6RemSteps = Math.ceil(maxOffsetRem / 6) * 6;
        
        setMaxOffset(maxOffsetIn6RemSteps);
    }, []);

    // Recalcular cuando cambie el tamaño de ventana
    useEffect(() => {
        calculateDimensions();
        
        const handleResize = () => calculateDimensions();
        window.addEventListener('resize', handleResize);
        
        return () => window.removeEventListener('resize', handleResize);
    }, [calculateDimensions, items]);

    // Navegar hacia la izquierda
    const goLeft = useCallback(() => {
        setCurrentOffset(prev => Math.max(0, prev - 6));
    }, []);

    // Navegar hacia la derecha
    const goRight = useCallback(() => {
        setCurrentOffset(prev => Math.min(maxOffset, prev + 6));
    }, [maxOffset]);

    const selectItem = (item: GiftCard, index: number) => {
        if (selectedItemIndex === index) {
            onClick(item);
            return;
        }
        const newOffset = Math.max(index - 2, 0) * 6;
        setCurrentOffset(Math.min(newOffset, maxOffset));
        setSelectedItemIndex(index);
    }

    // Verificar si se pueden mostrar botones
    const canGoLeft = currentOffset > 0;
    const canGoRight = currentOffset < maxOffset;

    return (
        <Container>
            <div ref={wrapperRef} className="carousel-wrapper">
                {canGoLeft && (
                    <Arrow left onClick={goLeft}>
                        <em className='fal fa-chevron-left'></em>
                    </Arrow>
                )}
                
                <div 
                    ref={containerRef}
                    className="carousel-container"
                    style={{
                    transform: `translateX(-${currentOffset}rem)`,
                    transition: 'transform 0.3s ease'
                    }}
                >
                    {items.map((item, index) => (
                        <Card key={index} onClick={() => selectItem(item, index)} selected={index === selectedItemIndex}>
                            <CardImage key={item.id} src={item.image} alt={item.name} />
                        </Card>
                    ))}
                </div>
                
                {canGoRight && (
                    <Arrow onClick={goRight}>
                        <em className='fal fa-chevron-right'></em>
                    </Arrow>
                )}
            </div>
        </Container>
    );
}

export default UserWalletListCarousel;